import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { Plus, Trash2, Users, UserPlus, UserMinus, Edit2, X, Check, Loader2 } from 'lucide-react';
import { formatDate } from '../lib/utils';

const CallerGroups = () => {
  const [groups, setGroups] = useState([]);
  const [callers, setCallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [selectedCallers, setSelectedCallers] = useState([]);
  const [expandedGroup, setExpandedGroup] = useState(null);
  // Loading states for async operations
  const [isCreating, setIsCreating] = useState(false);
  const [isAddingMembers, setIsAddingMembers] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [isRemovingMember, setIsRemovingMember] = useState(null);
  const user = authService.getCurrentUser();
  const token = authService.getToken();

  useEffect(() => {
    if (!user || !token) return;
    if (!['admin', 'manager'].includes(user?.role)) return;
    
    // Small delay to ensure auth is ready
    const timer = setTimeout(() => {
      fetchGroups();
      fetchCallers();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [user, token]);

  const fetchGroups = async () => {
    try {
      const response = await api.getCallerGroups();
      setGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch caller groups:', error);
      // Only show error toast for non-auth errors
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        toast.error('Failed to fetch caller groups');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCallers = async () => {
    try {
      const response = await api.getUsers('caller');
      setCallers(response.data);
    } catch (error) {
      console.error('Failed to fetch callers:', error);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    setIsCreating(true);
    try {
      await api.createCallerGroup(newGroupName, newGroupDesc);
      toast.success('Caller group created successfully');
      setNewGroupName('');
      setNewGroupDesc('');
      setShowCreateModal(false);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this caller group?')) return;

    setIsDeleting(groupId);
    try {
      await api.deleteCallerGroup(groupId);
      toast.success('Caller group deleted');
      fetchGroups();
    } catch (error) {
      toast.error('Failed to delete group');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleAddMembers = async () => {
    if (!selectedCallers.length) {
      toast.error('Please select at least one caller');
      return;
    }

    setIsAddingMembers(true);
    try {
      await api.addCallersToGroup(showAddMembersModal, selectedCallers);
      toast.success(`${selectedCallers.length} caller(s) added to group`);
      setShowAddMembersModal(null);
      setSelectedCallers([]);
      fetchGroups();
    } catch (error) {
      toast.error('Failed to add callers');
    } finally {
      setIsAddingMembers(false);
    }
  };

  const handleRemoveMember = async (groupId, callerId) => {
    setIsRemovingMember(callerId);
    try {
      await api.removeCallersFromGroup(groupId, [callerId]);
      toast.success('Caller removed from group');
      fetchGroups();
    } catch (error) {
      toast.error('Failed to remove caller');
    } finally {
      setIsRemovingMember(null);
    }
  };

  const getAvailableCallers = (group) => {
    const groupCallerIds = new Set(group?.caller_ids || []);
    return callers.filter(c => !groupCallerIds.has(c.id));
  };

  if (!['admin', 'manager'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <Users size={48} className="mx-auto text-slate-300 mb-4" />
        <p className="text-slate-600 font-body">You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600 font-body">Loading caller groups...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-headings uppercase tracking-tight text-slate-900">
            Caller Groups
          </h1>
          <p className="text-slate-600 font-body mt-1">
            Create and manage groups of callers for bulk lead assignment
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          data-testid="create-caller-group-btn"
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-all shadow-sm font-body"
        >
          <Plus size={18} />
          <span>Create Group</span>
        </button>
      </div>

      {/* Groups List */}
      {groups.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
          <Users size={64} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-600 font-body text-lg">No caller groups yet</p>
          <p className="text-slate-500 font-body text-sm mt-2">
            Create a group to organize your callers
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <div
              key={group.id}
              data-testid={`caller-group-${group.id}`}
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
            >
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-indigo-50">
                    <Users size={24} className="text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 font-body">{group.name}</h3>
                    <p className="text-sm text-slate-500 font-body">
                      {group.caller_count} caller(s) • Created {formatDate(group.created_at)}
                    </p>
                    {group.description && (
                      <p className="text-sm text-slate-600 font-body mt-1">{group.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAddMembersModal(group.id);
                    }}
                    data-testid={`add-members-${group.id}`}
                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                    title="Add Callers"
                  >
                    <UserPlus size={18} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGroup(group.id);
                    }}
                    disabled={isDeleting === group.id}
                    data-testid={`delete-group-${group.id}`}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                    title="Delete Group"
                  >
                    {isDeleting === group.id ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                  </button>
                </div>
              </div>

              {/* Expanded Members List */}
              {expandedGroup === group.id && (
                <div className="border-t border-slate-200 p-4 bg-slate-50">
                  <h4 className="text-sm font-semibold text-slate-700 font-body mb-3">Group Members</h4>
                  {group.callers?.length === 0 ? (
                    <p className="text-sm text-slate-500 font-body">No callers in this group yet</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {group.callers?.map((caller) => (
                        <div
                          key={caller.id}
                          className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200"
                        >
                          <div>
                            <p className="font-medium text-slate-900 font-body text-sm">{caller.name}</p>
                            <p className="text-xs text-slate-500 font-body">{caller.email}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveMember(group.id, caller.id)}
                            disabled={isRemovingMember === caller.id}
                            className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                            title="Remove from group"
                          >
                            {isRemovingMember === caller.id ? <Loader2 size={16} className="animate-spin" /> : <UserMinus size={16} />}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold font-headings text-slate-900">Create Caller Group</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateGroup} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-1">Group Name</label>
                <input
                  type="text"
                  data-testid="group-name-input"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g., UPSC Team, Hindi Callers"
                  className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 font-body mb-1">Description (Optional)</label>
                <input
                  type="text"
                  data-testid="group-desc-input"
                  value={newGroupDesc}
                  onChange={(e) => setNewGroupDesc(e.target.value)}
                  placeholder="Brief description of this group"
                  className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={isCreating}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-md hover:bg-slate-50 font-body disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  data-testid="submit-create-group"
                  className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-body disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isCreating && <Loader2 size={16} className="animate-spin" />}
                  {isCreating ? 'Creating...' : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Members Modal */}
      {showAddMembersModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold font-headings text-slate-900">Add Callers to Group</h2>
              <button onClick={() => { setShowAddMembersModal(null); setSelectedCallers([]); }} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-2 mb-4">
              {getAvailableCallers(groups.find(g => g.id === showAddMembersModal)).length === 0 ? (
                <p className="text-slate-500 font-body text-center py-4">All callers are already in this group</p>
              ) : (
                getAvailableCallers(groups.find(g => g.id === showAddMembersModal)).map((caller) => (
                  <label
                    key={caller.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedCallers.includes(caller.id)
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCallers.includes(caller.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCallers([...selectedCallers, caller.id]);
                        } else {
                          setSelectedCallers(selectedCallers.filter(id => id !== caller.id));
                        }
                      }}
                      className="w-4 h-4 text-indigo-600 rounded"
                    />
                    <div>
                      <p className="font-medium text-slate-900 font-body">{caller.name}</p>
                      <p className="text-sm text-slate-500 font-body">{caller.email}</p>
                    </div>
                  </label>
                ))
              )}
            </div>

            <div className="flex gap-3 pt-2 border-t border-slate-200">
              <button
                onClick={() => { setShowAddMembersModal(null); setSelectedCallers([]); }}
                disabled={isAddingMembers}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-md hover:bg-slate-50 font-body disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMembers}
                disabled={selectedCallers.length === 0 || isAddingMembers}
                data-testid="confirm-add-members"
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-body disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isAddingMembers && <Loader2 size={16} className="animate-spin" />}
                {isAddingMembers ? 'Adding...' : `Add ${selectedCallers.length} Caller(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallerGroups;
